import type { FolderNode } from "./types";
import type { Folder } from "../../services/api";

/** Trova un nodo nell'albero per id */
export function findNode(id: string, nodes: FolderNode[]): FolderNode | null {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.children) {
      const found = findNode(id, node.children);
      if (found) return found;
    }
  }
  return null;
}

/** Trova il parent di un nodo */
export function findParent(
  id: string,
  nodes: FolderNode[],
  parent: FolderNode | null = null
): { parent: FolderNode; index: number } | null {
  for (let i = 0; i < nodes.length; i++) {
    if (nodes[i].id === id) {
      return parent ? { parent, index: i } : null;
    }
    if (nodes[i].children) {
      const result = findParent(id, nodes[i].children!, nodes[i]);
      if (result) return result;
    }
  }
  return null;
}

/** Rimuove un nodo dall'albero */
export function removeNode(id: string, nodes: FolderNode[]): FolderNode[] {
  return nodes
    .filter((node) => node.id !== id)
    .map((node) => ({
      ...node,
      children: node.children ? removeNode(id, node.children) : undefined,
    }));
}

/** Aggiorna un nodo nell'albero */
export function updateNode(
  id: string,
  updater: (node: FolderNode) => FolderNode,
  nodes: FolderNode[]
): FolderNode[] {
  return nodes.map((node) => {
    if (node.id === id) return updater(node);
    return {
      ...node,
      children: node.children
        ? updateNode(id, updater, node.children)
        : undefined,
    };
  });
}

/** Inserisce un nodo in una posizione specifica */
export function insertNode(
  nodes: FolderNode[],
  newNode: FolderNode,
  targetIndex: number,
  insertAfter: boolean
): FolderNode[] {
  const newNodes = [...nodes];
  const insertIndex = insertAfter ? targetIndex + 1 : targetIndex;
  newNodes.splice(insertIndex, 0, newNode);
  return newNodes;
}

/** Verifica se un nodo è discendente di un altro */
export function isDescendant(
  descendantId: string,
  ancestor: FolderNode
): boolean {
  if (ancestor.id === descendantId) return true;
  if (ancestor.children) {
    return ancestor.children.some((child) => isDescendant(descendantId, child));
  }
  return false;
}

/** Converte Folder (API) in FolderNode (UI). Per nodi cartella: id = folder.id; per chat in cartella: id = chat_id. */
export function convertFolderToNode(folder: Folder): FolderNode<Folder> {
  return {
    id: folder.chat_id ?? folder.id,
    label: folder.name,
    type: folder.type === 0 ? "folder" : "conversation",
    data: folder,
    children: folder.children
      ? folder.children.map(convertFolderToNode)
      : undefined,
  };
}
